import { escapeHtml } from '@shared/utils';

export type TemporalFieldType = 'DATE' | 'TIME' | 'DATETIME';
export type TemporalFieldFormat = { type: TemporalFieldType; datePattern?: string; timePattern?: string };
export type DecimalFieldFormat = {
  maxIntegerDigits: number | null;
  maxDecimalDigits: number | null;
  decimalSeparator: ',' | '.';
  thousandSeparator: '.' | ',';
};

export function onlyDigits(value: string): string {
  return String(value || '').replace(/\D/g, '');
}

export function applyThousandsSeparator(integerDigits: string, thousandSeparator: '.' | ','): string {
  const digits = onlyDigits(integerDigits);
  if (!digits) {
    return '';
  }
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
}

// --- Máscara de CPF/CNPJ ---

export function formatCpf(value: string): string {
  const digits = onlyDigits(value).slice(0, 11);
  if (!digits) {
    return '';
  }

  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  }
  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

export function formatCnpj(value: string): string {
  const digits = onlyDigits(value).slice(0, 14);
  if (!digits) {
    return '';
  }

  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 5) {
    return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  }
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  }
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

export function applyDocumentMask(value: string, maskType: 'CPF' | 'CNPJ' | 'CPF_CNPJ'): string {
  if (maskType === 'CPF') {
    return formatCpf(value.slice(0, 11));
  }
  if (maskType === 'CNPJ') {
    return formatCnpj(value.slice(0, 14));
  }

  const digits = value.slice(0, 14);
  if (digits.length > 11) {
    return formatCnpj(digits);
  }
  return formatCpf(digits);
}

// --- Formatação temporal (data/hora/data-hora) ---

export function normalizeTimePattern(pattern: any): string {
  const normalized = String(pattern || '').trim().toLowerCase();
  if (!normalized) {
    return 'hh:mm:ss';
  }
  if (!/[hms]/i.test(normalized)) {
    return 'hh:mm:ss';
  }
  return normalized;
}

export function normalizeDatePattern(pattern: any): string {
  const normalized = String(pattern || '').trim().toLowerCase();
  if (!normalized) {
    return 'dd/mm/yyyy';
  }
  if (!/[dmy]/i.test(normalized)) {
    return 'dd/mm/yyyy';
  }
  return normalized;
}

export function applyTemporalPattern(digitsValue: string, pattern: string): string {
  const digits = onlyDigits(digitsValue);
  const tokens = pattern.match(/[a-z]/gi) || [];
  const maxDigits = tokens.length;
  const limitedDigits = maxDigits > 0 ? digits.slice(0, maxDigits) : digits;
  if (!limitedDigits) {
    return '';
  }

  let result = '';
  let digitIndex = 0;
  for (let i = 0; i < pattern.length; i += 1) {
    const patternChar = pattern[i];
    const isToken = /[a-z]/i.test(patternChar);
    if (isToken) {
      if (digitIndex >= limitedDigits.length) {
        break;
      }
      result += limitedDigits[digitIndex];
      digitIndex += 1;
      continue;
    }

    if (digitIndex > 0 && digitIndex < limitedDigits.length) {
      result += patternChar;
    }
  }

  return result;
}

export function formatTime(value: string, pattern?: string): string {
  const normalizedPattern = normalizeTimePattern(pattern);
  return applyTemporalPattern(onlyDigits(value), normalizedPattern);
}

export function formatDateTime(value: string, datePattern?: string, timePattern?: string): string {
  const normalizedDatePattern = normalizeDatePattern(datePattern);
  const normalizedTimePattern = normalizeTimePattern(timePattern);
  const combinedPattern = `${normalizedDatePattern} ${normalizedTimePattern}`.trim();
  return applyTemporalPattern(onlyDigits(value), combinedPattern);
}

export function parseDateValue(value: string): string | null {
  // dd/MM/yyyy → yyyy-MM-dd
  const brMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) {
    return `${brMatch[3]}-${brMatch[2]}-${brMatch[1]}`;
  }

  // Tenta Date parse genérico
  const date = new Date(value);
  if (!isNaN(date.getTime())) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  return null;
}

export function parseTemporalValue(value: string, temporalType: 'DATE' | 'TIME' | 'DATETIME'): string | null {
  if (temporalType === 'DATE') {
    return parseDateValue(value);
  }

  const trimmed = String(value || '').trim();
  if (!trimmed) {
    return null;
  }

  if (temporalType === 'TIME') {
    const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (timeMatch) {
      const h = String(Number(timeMatch[1])).padStart(2, '0');
      const m = timeMatch[2];
      const s = timeMatch[3];
      return s ? `${h}:${m}:${s}` : `${h}:${m}`;
    }
    return null;
  }

  const brDateTime = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+|T)(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (brDateTime) {
    const day = brDateTime[1];
    const month = brDateTime[2];
    const year = brDateTime[3];
    const hour = String(Number(brDateTime[4])).padStart(2, '0');
    const minute = brDateTime[5];
    const second = brDateTime[6] ? brDateTime[6] : '00';
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  }

  const isoDateTime = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+|T)(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (isoDateTime) {
    const second = isoDateTime[6] ? isoDateTime[6] : '00';
    return `${isoDateTime[3]}/${isoDateTime[2]}/${isoDateTime[1]} ${isoDateTime[4]}:${isoDateTime[5]}:${second}`;
  }

  const date = new Date(trimmed);
  if (!isNaN(date.getTime())) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${d}/${m}/${y} ${hh}:${mm}:${ss}`;
  }

  return null;
}

// --- Formatação decimal ---

export function computeDecimalMaxDisplayLength(config: DecimalFieldFormat): number {
  const integerDigits = typeof config.maxIntegerDigits === 'number' && config.maxIntegerDigits > 0
    ? config.maxIntegerDigits : 9;
  const fractionDigits = typeof config.maxDecimalDigits === 'number' && config.maxDecimalDigits >= 0
    ? config.maxDecimalDigits : 2;
  const thousandSeps = Math.max(0, Math.floor((integerDigits - 1) / 3));
  const decimalSep = fractionDigits > 0 ? 1 : 0;
  return integerDigits + thousandSeps + decimalSep + fractionDigits;
}

export function getDecimalFieldFormat(configuracoes: any): DecimalFieldFormat {
  const maxChars = Number(configuracoes?.maxChars);
  const maxDecimalChars = Number(configuracoes?.maxDecimalChars);
  const decimalPattern = String(configuracoes?.decimal || configuracoes?.formato || '').trim();
  const decimalSeparator = decimalPattern
    ? (decimalPattern.includes(',') ? ',' : '.')
    : ',';
  const thousandSeparator = decimalSeparator === ',' ? '.' : ',';

  const maxIntegerDigits = Number.isFinite(maxChars) && maxChars > 0
    ? Math.floor(maxChars)
    : null;
  const maxDecimalDigits = Number.isFinite(maxDecimalChars) && maxDecimalChars >= 0
    ? Math.floor(maxDecimalChars)
    : null;

  return {
    maxIntegerDigits,
    maxDecimalDigits,
    decimalSeparator,
    thousandSeparator
  };
}

export function pickDecimalFormat(current: DecimalFieldFormat | undefined, incoming: DecimalFieldFormat): DecimalFieldFormat {
  if (!current) {
    return incoming;
  }

  // Evita que outro campo homônimo com limite menor reduza a máscara.
  const currentInteger = typeof current.maxIntegerDigits === 'number' ? current.maxIntegerDigits : -1;
  const incomingInteger = typeof incoming.maxIntegerDigits === 'number' ? incoming.maxIntegerDigits : -1;
  const currentDecimal = typeof current.maxDecimalDigits === 'number' ? current.maxDecimalDigits : -1;
  const incomingDecimal = typeof incoming.maxDecimalDigits === 'number' ? incoming.maxDecimalDigits : -1;
  const incomingIsWider = incomingInteger > currentInteger || (incomingInteger === currentInteger && incomingDecimal > currentDecimal);

  return incomingIsWider ? incoming : current;
}

export function formatDecimal(value: string, config: DecimalFieldFormat, fillDecimals: boolean): string {
  const raw = String(value || '');
  const allowNegative = false;
  const negative = allowNegative && raw.trim().startsWith('-');
  const integerLimit = typeof config.maxIntegerDigits === 'number' && config.maxIntegerDigits >= 0 ? config.maxIntegerDigits : 9;
  const fractionLimit = typeof config.maxDecimalDigits === 'number' && config.maxDecimalDigits >= 0 ? config.maxDecimalDigits : 2;
  const totalDigits = integerLimit + fractionLimit;

  let decimalSeparatorAdded = false;
  let integerDigits = '';
  let decimalDigits = '';

  for (let i = 0; i < raw.length; i += 1) {
    if (integerDigits.length + decimalDigits.length >= totalDigits) {
      break;
    }
    if (fractionLimit > 0 && decimalDigits.length >= fractionLimit) {
      break;
    }

    const char = raw[i];
    if (!decimalSeparatorAdded && char === config.decimalSeparator && fractionLimit > 0) {
      decimalSeparatorAdded = true;
      continue;
    }

    if (char < '0' || char > '9') {
      continue;
    }

    if (!decimalSeparatorAdded && integerDigits.length === integerLimit) {
      if (fractionLimit <= 0) {
        break;
      }
      decimalSeparatorAdded = true;
    }

    if (integerDigits.length === 1 && integerDigits[0] === '0' && !decimalSeparatorAdded) {
      integerDigits = '';
    }

    if (decimalSeparatorAdded) {
      decimalDigits += char;
    } else {
      integerDigits += char;
    }
  }

  if (fillDecimals) {
    if (!integerDigits && integerLimit !== 0) {
      integerDigits = '0';
    }
    if (!decimalSeparatorAdded && fractionLimit > 0) {
      decimalSeparatorAdded = true;
    }
    while (decimalDigits.length < fractionLimit) {
      decimalDigits += '0';
    }
  }

  if (!integerDigits && !decimalDigits) {
    return '';
  }

  const integerPart = applyThousandsSeparator(integerDigits, config.thousandSeparator);
  return `${negative ? '-' : ''}${integerPart}${decimalSeparatorAdded ? config.decimalSeparator : ''}${decimalDigits}`;
}

// --- Normalização da configuração do grid (sem DOM) ---

export interface CpaGridNormalizedConfig {
  /** Mesma referência recebida (objeto ou array) — os campos internos são mutados in-place. */
  config: any;
  selectValueByTitle: Record<string, string>;
  maskTypeByTitle: Record<string, 'CPF' | 'CNPJ' | 'CPF_CNPJ'>;
  temporalTypeByTitle: Record<string, TemporalFieldType>;
  temporalFormatByTitle: Record<string, TemporalFieldFormat>;
  decimalFormatByTitle: Record<string, DecimalFieldFormat>;
}

/**
 * Normaliza a config bruta do cpa-grid (campos SELECT/SELECT_MULTIPLE/CPF/CNPJ/
 * DATE/TIME/DATETIME/DECIMAL) antes de compilar o template AngularJS, e monta os
 * mapas por título usados pelos enhancers de DOM (máscara, validação, etc.). Não
 * toca em DOM — só lê/muta a árvore de config recebida (mutação in-place, retorna
 * a mesma referência de topo).
 */
export function normalizeCpaGridConfig(config: any): CpaGridNormalizedConfig {
  const selectValueByTitle: Record<string, string> = {};
  const maskTypeByTitle: Record<string, 'CPF' | 'CNPJ' | 'CPF_CNPJ'> = {};
  const temporalTypeByTitle: Record<string, TemporalFieldType> = {};
  const temporalFormatByTitle: Record<string, TemporalFieldFormat> = {};
  const decimalFormatByTitle: Record<string, DecimalFieldFormat> = {};

  if (!config) {
    return { config, selectValueByTitle, maskTypeByTitle, temporalTypeByTitle, temporalFormatByTitle, decimalFormatByTitle };
  }

  const extractValue = (value: any): any => {
    if (value && typeof value === 'object') {
      if (Object.prototype.hasOwnProperty.call(value, 'valor')) {
        return value.valor;
      }
      if (Object.prototype.hasOwnProperty.call(value, 'value')) {
        return value.value;
      }
      if (Object.prototype.hasOwnProperty.call(value, 'id')) {
        return value.id;
      }
    }
    return value;
  };

  const tryParseArray = (value: any): any[] => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
    }
    return [];
  };

  const normalizeCampo = (campo: any): void => {
    if (!campo) {
      return;
    }

    const formato = String(campo.formato || '').toUpperCase();
    const formatoNormalizado = formato.replace(/\s/g, '');
    const values = campo?.configuracoes?.values;

    if (campo.titulo) {
      const tituloCampo = String(campo.titulo).trim();
      if (formatoNormalizado === 'CPF') {
        maskTypeByTitle[tituloCampo] = 'CPF';
      } else if (formatoNormalizado === 'CNPJ') {
        maskTypeByTitle[tituloCampo] = 'CNPJ';
      } else if (formatoNormalizado === 'CPF,CNPJ' || formatoNormalizado === 'CNPJ,CPF') {
        maskTypeByTitle[tituloCampo] = 'CPF_CNPJ';
      }

      if (formatoNormalizado === 'DATE' || formatoNormalizado === 'DATA') {
        temporalTypeByTitle[tituloCampo] = 'DATE';
        temporalFormatByTitle[tituloCampo] = {
          type: 'DATE',
          datePattern: normalizeDatePattern(campo?.configuracoes?.date)
        };
      } else if (formatoNormalizado === 'TIME') {
        temporalTypeByTitle[tituloCampo] = 'TIME';
        temporalFormatByTitle[tituloCampo] = {
          type: 'TIME',
          timePattern: normalizeTimePattern(campo?.configuracoes?.time)
        };
      } else if (formatoNormalizado === 'DATETIME') {
        temporalTypeByTitle[tituloCampo] = 'DATETIME';
        temporalFormatByTitle[tituloCampo] = {
          type: 'DATETIME',
          datePattern: normalizeDatePattern(campo?.configuracoes?.date),
          timePattern: normalizeTimePattern(campo?.configuracoes?.time)
        };
      } else if (formatoNormalizado === 'DECIMAL' || formatoNormalizado === 'NUMERIC' || formatoNormalizado === 'NUMBER') {
        const decimalFormat = getDecimalFieldFormat(campo?.configuracoes);
        const existingFormat = decimalFormatByTitle[tituloCampo];
        decimalFormatByTitle[tituloCampo] = pickDecimalFormat(existingFormat, decimalFormat);

        // Normaliza o valor inicial (Number ou string com ponto, vindo da API) para exibição
        // com vírgula — mesmo comportamento do ui-number $render.
        if (campo.valor !== null && campo.valor !== undefined && String(campo.valor).trim() !== '') {
          const rawStr = String(campo.valor).trim();
          const num = parseFloat(rawStr.replace(',', '.'));
          if (!isNaN(num)) {
            campo.valor = String(num).replace('.', decimalFormat.decimalSeparator);
          }
        }

        // maxChars deve refletir o tamanho da string *formatada* (dígitos + separadores), não só
        // os dígitos inteiros — senão o maxlength nativo do input bloqueia dígitos válidos.
        // maxDecimalChars precisa existir para o normalizador de save reconhecer o campo como
        // decimal e não enviar { formato:'TEXT', valor:"2,50" } (retorna 422).
        if (!campo.configuracoes || typeof campo.configuracoes !== 'object') {
          campo.configuracoes = {};
        }
        campo.configuracoes = {
          ...campo.configuracoes,
          maxChars: computeDecimalMaxDisplayLength(decimalFormat),
          maxDecimalChars: Object.prototype.hasOwnProperty.call(campo.configuracoes, 'maxDecimalChars')
            ? campo.configuracoes.maxDecimalChars
            : (typeof decimalFormat.maxDecimalDigits === 'number' ? decimalFormat.maxDecimalDigits : 2)
        };

        // O componente legado de decimal não renderiza no bridge em alguns cenários.
        // Forçamos renderização textual e preservamos o formato original via configuração.
        campo.formato = 'TEXT';
      }
    }
    if (!Array.isArray(values) || !values.length) {
      return;
    }

    if (formato === 'SELECT') {
      const valorSelecionado = extractValue(campo.valor);
      const selecionado = values.find((item: any) => String(item?.valor) === String(valorSelecionado));
      if (selecionado) {
        campo.valor = selecionado.valor;
        if (campo.titulo) {
          selectValueByTitle[String(campo.titulo).trim()] = String(selecionado.valor);
        }
      }
      return;
    }

    if (formato === 'SELECT_MULTIPLE') {
      const selecionados = tryParseArray(campo.valor).map((valor: any) => extractValue(valor));
      campo.valor = selecionados
        .map((valor: any) => values.find((item: any) => String(item?.valor) === String(valor)))
        .filter((item: any) => !!item)
        .map((item: any) => item.valor);
    }
  };

  const normalizeAgrupadores = (agrupadores: any[]): void => {
    agrupadores.forEach((agrupador: any) => {
      (agrupador?.campos || []).forEach((campo: any) => normalizeCampo(campo));
    });
  };

  if (Array.isArray(config)) {
    config.forEach((item) => normalizeAgrupadores(item?.agrupadores || []));
    return { config, selectValueByTitle, maskTypeByTitle, temporalTypeByTitle, temporalFormatByTitle, decimalFormatByTitle };
  }

  normalizeAgrupadores(config?.agrupadores || []);
  return { config, selectValueByTitle, maskTypeByTitle, temporalTypeByTitle, temporalFormatByTitle, decimalFormatByTitle };
}

// --- Diversos ---

export function flattenConfiguracoes(config: any): any[] {
  if (!config) {
    return [];
  }

  if (Array.isArray(config)) {
    return config;
  }

  return [config];
}

export function isRequiredFieldEmpty(fieldEl: HTMLElement): boolean {
  const tagName = fieldEl.tagName.toLowerCase();

  if (tagName === 'select') {
    const selectEl = fieldEl as HTMLSelectElement;
    if (selectEl.multiple) {
      return Array.from(selectEl.selectedOptions).length === 0;
    }
    return String(selectEl.value || '').trim().length === 0;
  }

  if (tagName === 'input' || tagName === 'textarea') {
    const inputEl = fieldEl as HTMLInputElement | HTMLTextAreaElement;
    return String(inputEl.value || '').trim().length === 0;
  }

  return false;
}

/**
 * Escapa valor pra uso em atributo HTML (data-*, aria-label). Diferente de
 * `escapeHtml` (@shared/utils), que escapa pra uso em conteúdo de texto.
 */
export function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export interface CpaFallbackOption {
  value: string;
  label: string;
}

/**
 * HTML das opções disponíveis (não selecionadas) do menu do fallback de
 * select múltiplo (quando select2/jQuery não está carregado).
 */
export function buildMultiFallbackOptionsHtml(options: CpaFallbackOption[]): string {
  return options.length
    ? options
        .map((opt) => `<button type="button" class="cpa-multi-fallback-option" data-option-value="${escapeAttr(opt.value)}">${escapeHtml(opt.label)}</button>`)
        .join('')
    : `<span class="cpa-multi-fallback-empty">Nenhum resultado encontrado</span>`;
}

/**
 * HTML dos chips das opções já selecionadas do fallback de select múltiplo.
 */
export function buildMultiFallbackChipsHtml(labels: string[]): string {
  return labels
    .map((label) => `<li class="select2-search-choice"><div>${escapeHtml(label)}</div><button type="button" class="select2-search-choice-close" data-chip-label="${escapeAttr(label)}" tabindex="-1" aria-label="Remover ${escapeAttr(label)}"></button></li>`)
    .join('');
}

/**
 * HTML completo do container do fallback de select múltiplo (chips + campo
 * de busca desabilitado + menu de opções disponíveis).
 */
export function buildMultiFallbackContainerHtml(selectedLabels: string[], availableOptions: CpaFallbackOption[]): string {
  const optionsHtml = buildMultiFallbackOptionsHtml(availableOptions);
  const selectedChips = buildMultiFallbackChipsHtml(selectedLabels);
  return `
        <div class="cpa-multi-fallback-control" role="button" tabindex="0" aria-label="Selecionar opções">
          <ul class="select2-choices">
            ${selectedChips}
            <li class="select2-search-field"><input type="text" class="select2-input" tabindex="-1" readonly placeholder="${selectedLabels.length ? '' : 'Selecionar'}"></li>
          </ul>
        </div>
        <div class="cpa-multi-fallback-menu">${optionsHtml}</div>
      `;
}
